import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AtiraColorPicker } from "../../../components/AtiraColorPicker";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { TagChip } from "../../../components/TagChip";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Tag } from "../../../model/tag/Tag";
import { CreateTagDto } from "../../../model/tag/dto/CreateTagDto";
import { useAppDispatch } from "../../../redux/store";
import { tagActions } from "../../../redux/tag/tag.slice";
import { TestIDs } from "../../../tests/types/testIds";
import { AtiraToast } from "../../../utils/AtiraToast";
import { randomHexColor } from "../../../utils/Color";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";

type SettingsTagCreateEditModalProps = {
  open: boolean;
  onClose: VoidFunction;
  /** Left out to create a new tag */
  tag?: Tag | null;
};

export const SettingsTagCreateEditModal: React.FC<
  SettingsTagCreateEditModalProps
> = ({ open, onClose, tag }) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { control, handleSubmit, reset, setError, watch } =
    useForm<CreateTagDto>();

  const name = watch("name");
  const color = watch("color");

  useEffect(() => {
    if (open) {
      reset({
        name: tag?.name || "",
        color: tag?.color || randomHexColor(),
      });
    }
  }, [open, reset, tag]);

  const onSave = async (dto: CreateTagDto) => {
    try {
      setLoading(true);

      const values = { name: dto.name.trim(), color: dto.color };

      if (tag) {
        await dispatch(
          tagActions.updateTag({ tagId: tag._id, ...values }),
        ).unwrap();
      } else {
        await dispatch(tagActions.createTag(values)).unwrap();
      }

      AtiraToast.success(
        t(
          tag ? "settings.tags.update.success" : "settings.tags.create.success",
        ),
      );
      onClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(tag ? "settings.tags.edit.title" : "settings.tags.create.title")}
      destroyOnHidden
    >
      <Flex flexDirection="column" gap="m" width="100%">
        <Controller
          name="name"
          control={control}
          rules={{
            validate: (value) =>
              value?.trim().length ? true : t("settings.tags.name.required"),
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t("common.name")}
              value={value}
              onChange={onChange}
              maxLength={Lengths.NAME}
              required
              placeholder={t("settings.tags.name.placeholder")}
              valid={!error}
              errorMessage={error?.message}
              dataTestId={TestIDs.general.settings.tags.modal.name}
            />
          )}
        />

        <Controller
          name="color"
          control={control}
          rules={{ required: t("settings.tags.color.required") }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraColorPicker
              title={t("settings.tags.color.title")}
              value={value}
              onChange={(e: any) => onChange(e?.target?.value || "")}
              valid={!error}
              errorMessage={error?.message}
              dataTestId={TestIDs.general.settings.tags.modal.color}
            />
          )}
        />

        <Flex flexDirection="column" gap="xs">
          <Text fontSize="s" color="darkerSub">
            {t("settings.tags.preview")}
          </Text>
          <Flex>
            {color ? (
              <TagChip
                tag={{
                  name: name?.trim() || t("settings.tags.name.placeholder"),
                  color,
                }}
              />
            ) : null}
          </Flex>
        </Flex>

        <Button
          onClick={handleSubmit(onSave)}
          loading={loading}
          margin="none"
          dataTestId={TestIDs.general.settings.tags.modal.save}
        >
          {t(tag ? "common.save" : "common.create")}
        </Button>
      </Flex>
    </Modal>
  );
};
